import { useEffect, useRef } from 'react';

/**
 * Gives the surrounding `role="dialog"` element an accessible name.
 *
 * Both dialog shells used on the product detail (the styleguide `Modal` and the
 * `BottomSheet` drawer) render the `role="dialog"` element themselves and do not
 * forward aria attributes to it, so the name has to be attached from the content
 * side. Render the returned ref on any element inside the dialog and pass the id
 * of the element holding its title.
 *
 * @param labelId - id of the element naming the dialog; pass `undefined` to skip
 *   (e.g. for a screen that renders no title).
 */
export const useLabelEnclosingDialog = (labelId?: string) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dialog = ref.current?.closest('[role="dialog"]');

    if (!dialog || !labelId) {
      return;
    }

    const previousLabelledBy = dialog.getAttribute('aria-labelledby');
    dialog.setAttribute('aria-labelledby', labelId);

    return () => {
      if (previousLabelledBy === null) {
        dialog.removeAttribute('aria-labelledby');
        return;
      }
      dialog.setAttribute('aria-labelledby', previousLabelledBy);
    };
  }, [labelId]);

  return ref;
};
